import { Injectable } from "@angular/core";
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from "@angular/common/http";
import { Observable, from, switchMap, catchError, of } from "rxjs";
import { AuthService } from "../service/auth.service";

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip token injection for public endpoints and Keycloak URLs
    if (this.isPublicEndpoint(request.url)) {
      return next.handle(request);
    }

    // Check if user is logged in via Keycloak
    if (!this.authService.isLoggedIn()) {
      return next.handle(request);
    }

    // Get token from Keycloak and add to request.
    // catchError is placed BEFORE switchMap so it only handles getToken() failures,
    // not HTTP errors from next.handle(request). If it were after switchMap, a failed
    // HTTP request would be silently retried without auth, causing duplicate requests
    // and notifications.
    return from(this.authService.getToken()).pipe(
      catchError((error) => {
        // Token retrieval failed — proceed without auth rather than blocking the request
        console.warn('Could not get token, proceeding without auth:', error?.message || error);
        return of(null as unknown as string);
      }),
      switchMap((token: string | null) => {
        if (token) {
          request = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
            },
          });
        }
        return next.handle(request);
      })
    );
  }

  /**
   * Check if the URL is a public endpoint that doesn't need authentication
   */
  private isPublicEndpoint(url: string): boolean {
    // Skip API URLs - they always need authentication
    if (url.includes('/api/')) {
      return false;
    }

    const publicPaths = [
      '/assets/',
      '/i18n/',
      '.json',
      '.html',
      '.css',
      '.js',
      '.png',
      '.jpg',
      '.svg',
      // Keycloak endpoints - let keycloak-js handle these
      '/realms/',
      '/protocol/openid-connect/',
      'localhost:8080',
    ];
    return publicPaths.some(path => url.includes(path));
  }
}
